/**
 * URL hiển thị ảnh/video trong bubble chat — ưu tiên CDN, fallback blob preview.
 * @param {Record<string, unknown>|null|undefined} item
 * @param {{ preferDirectPath?: boolean }|null|undefined} [options]
 * @returns {string}
 */
export function resolveChatMediaDisplaySrc(item, options = null) {
  if (!item || typeof item !== 'object') {
    return '';
  }

  const preferDirectPath = !!(options && options.preferDirectPath);
  const path = String(item.path || '').trim();
  const rawUrl = String(item.url || '').trim();
  const thumbObj = item.thumbnail && typeof item.thumbnail === 'object' ? item.thumbnail : null;
  const mediumThumb = String((thumbObj && thumbObj.medium_thumb) || '').trim();
  const smallThumb = String((thumbObj && thumbObj.small_thumb) || '').trim();
  const thumbUrl = String((thumbObj && thumbObj.url) || '').trim();

  const isBlob = (url) => url.startsWith('blob:');
  const isUsable = (url) => url.length > 0;
  const isApiDownloadUrl = (url) => /\/api\/v\d+\/download\//i.test(url)
    || /\/api\/.*\/media\//i.test(url)
    || /\/media\/download/i.test(url);
  const isPackageMediaPath = (url) => /\/packages\//i.test(url);

  // Preview optimistic (blob) — chỉ có trên path local.
  if (isUsable(path) && isBlob(path)) {
    return path;
  }

  // Sticker / package: thumb thường chưa generate → dùng file gốc (path 200, thumb 404).
  if ((preferDirectPath || isPackageMediaPath(path)) && isUsable(path) && !isBlob(path)) {
    return path;
  }

  if (isUsable(mediumThumb) && !(isPackageMediaPath(path) && mediumThumb !== path)) {
    return mediumThumb;
  }
  if (isUsable(smallThumb) && !(isPackageMediaPath(path) && smallThumb !== path)) {
    return smallThumb;
  }
  if (isUsable(thumbUrl)) {
    return thumbUrl;
  }
  if (isUsable(path)) {
    return path;
  }
  if (isUsable(rawUrl) && !isApiDownloadUrl(rawUrl)) {
    return rawUrl;
  }
  return '';
}

/**
 * Fallback khi `<img>` load thumb/package URL lỗi (404).
 * @param {Event} event
 * @param {Record<string, unknown>|null|undefined} item
 */
export function fallbackChatMediaImageOnError(event, item) {
  const target = event && event.target;
  if (!target || !item || typeof item !== 'object') {
    return;
  }
  const path = String(item.path || '').trim();
  const rawUrl = String(item.url || '').trim();
  const isApiDownloadUrl = (url) => /\/api\/v\d+\/download\//i.test(url)
    || /\/api\/.*\/media\//i.test(url)
    || /\/media\/download/i.test(url);
  const fallback = path || (!isApiDownloadUrl(rawUrl) ? rawUrl : '');
  if (fallback && target.src !== fallback) {
    target.src = fallback;
  }
}

/**
 * Snapshot file metadata cho tin optimistic — tách khỏi composer medias.
 * @param {Array<Record<string, unknown>>|null|undefined} medias
 * @returns {Array<Record<string, unknown>>}
 */
export function cloneMediaFilesForOptimisticMessage(medias) {
  if (!Array.isArray(medias)) {
    return [];
  }

  return medias.map((media) => {
    if (!media || typeof media !== 'object') {
      return media;
    }

    const clone = { ...media };
    if (media.thumbnail && typeof media.thumbnail === 'object') {
      clone.thumbnail = { ...media.thumbnail };
    }

    if (media._previewObjectUrl) {
      clone._previewObjectUrl = media._previewObjectUrl;
      delete media._previewObjectUrl;
    }

    return clone;
  });
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} files
 */
export function revokeOptimisticMediaPreviewUrls(files) {
  if (!Array.isArray(files)) {
    return;
  }
  files.forEach((file) => {
    if (file && file._previewObjectUrl) {
      try {
        URL.revokeObjectURL(file._previewObjectUrl);
      } catch (e) {
        void e;
      }
      delete file._previewObjectUrl;
    }
  });
}
